import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Baby, Calendar, Mail, GitMerge, Loader2, RefreshCw, X, Check, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const BADGE_MAP = {
  exact_name: { label: 'Exact Name', color: 'bg-red-500' },
  same_dob: { label: 'Same DOB', color: 'bg-orange-500' },
  name_match: { label: 'Similar Name', color: 'bg-amber-500' },
  same_parent_email: { label: 'Same Parent Email', color: 'bg-purple-500' },
};

export default function AthleteDuplicates() {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPair, setSelectedPair] = useState(null);
  const [merging, setMerging] = useState(false);
  const [keepChildId, setKeepChildId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [dismissingPair, setDismissingPair] = useState(null);

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/children/find-duplicates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDuplicates(response.data.duplicates || []);
    } catch (err) {
      console.error('Failed to find athlete duplicates:', err);
      if (err.response?.status === 403) {
        toast.error('Only EPP Staff can access duplicate detection');
      } else {
        toast.error('Failed to find athlete duplicates');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const merge = async () => {
    if (!selectedPair || !keepChildId) return;
    const deleteChildId = keepChildId === selectedPair.child1.id
      ? selectedPair.child2.id
      : selectedPair.child1.id;

    setMerging(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/children/merge`, {
        keep_child_id: keepChildId,
        delete_child_id: deleteChildId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Athletes merged successfully');
      setShowConfirm(false);
      setSelectedPair(null);
      setKeepChildId(null);
      fetchDuplicates();
    } catch (err) {
      console.error('Athlete merge error:', err);
      toast.error(err.response?.data?.detail || 'Failed to merge athletes');
    } finally {
      setMerging(false);
    }
  };

  const dismiss = async (pair) => {
    const pairKey = `${pair.child1.id}_${pair.child2.id}`;
    setDismissingPair(pairKey);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/children/dismiss-duplicate`, {
        child1_id: pair.child1.id,
        child2_id: pair.child2.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Suggestion dismissed — it won't appear again");
      setDuplicates(prev => prev.filter(d =>
        !(d.child1.id === pair.child1.id && d.child2.id === pair.child2.id)
      ));
    } catch (err) {
      console.error('Dismiss error:', err);
      toast.error('Failed to dismiss suggestion');
    } finally {
      setDismissingPair(null);
    }
  };

  const selectForMerge = (pair) => {
    setSelectedPair(pair);
    setKeepChildId(null);
    setShowConfirm(true);
  };

  const getBadges = (reasons) =>
    (reasons || []).map(r => BADGE_MAP[r] || { label: r, color: 'bg-gray-500' });

  return (
    <div className="border-t border-zinc-700/50 pt-4 mt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-pink-500/20 to-rose-500/20 rounded-xl flex items-center justify-center">
            <Baby className="h-5 w-5 text-pink-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Find & Merge Duplicate Athletes</h3>
            <p className="text-xs text-zinc-500">Match athletes by name + date of birth or shared parent email</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchDuplicates}
          disabled={loading}
          className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300"
          data-testid="scan-athlete-duplicates-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-2">Scan</span>
        </Button>
      </div>

      <p className="text-sm text-zinc-400 mb-3" data-testid="athlete-duplicates-count">
        {duplicates.length} potential duplicate{duplicates.length !== 1 ? 's' : ''} found
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-pink-400" />
        </div>
      ) : duplicates.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">
          <Baby className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No duplicate athletes detected</p>
          <p className="text-xs mt-1">Click Scan to re-check. Athletes are matched by name + date of birth.</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
          {duplicates.map((pair, idx) => (
            <div
              key={`child-${idx}`}
              className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50 hover:border-pink-500/50 transition-all"
              data-testid={`child-duplicate-pair-${idx}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[pair.child1, pair.child2].map((c, ci) => (
                    <div key={ci} className="bg-zinc-900/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white font-bold text-xs">
                          {c.first_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-white text-sm">{c.full_name}</p>
                          <p className="text-xs text-zinc-500 flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {c.date_of_birth || 'No DOB'}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 text-xs">
                        {c.parent_email && (
                          <span className="px-2 py-0.5 rounded bg-zinc-700 text-zinc-300 flex items-center gap-1 truncate max-w-[140px]">
                            <Mail className="h-3 w-3" /> {c.parent_email}
                          </span>
                        )}
                        {c.team_name && (
                          <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">{c.team_name}</span>
                        )}
                        <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">{c.measurements_count} meas</span>
                        <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300">{c.tests_count} tests</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col items-center gap-2">
                  <Button
                    onClick={() => selectForMerge(pair)}
                    className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm"
                    data-testid={`merge-athlete-btn-${idx}`}
                  >
                    <GitMerge className="h-4 w-4 mr-1" />
                    Merge
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismiss(pair)}
                    disabled={dismissingPair === `${pair.child1.id}_${pair.child2.id}`}
                    className="text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700"
                    data-testid={`dismiss-athlete-btn-${idx}`}
                  >
                    {dismissingPair === `${pair.child1.id}_${pair.child2.id}` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <span className="text-xs text-zinc-500">Match:</span>
                {getBadges(pair.match_reasons).map((badge, bidx) => (
                  <span key={bidx} className={`px-2 py-0.5 rounded text-xs text-white ${badge.color}`}>
                    {badge.label}
                  </span>
                ))}
                <span className="text-xs text-zinc-500 ml-2">
                  Name similarity: {Math.round(pair.similarity.name * 100)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Merge Confirmation Modal */}
      {showConfirm && selectedPair && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => setShowConfirm(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg p-6 relative z-[10000]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <GitMerge className="h-5 w-5 text-pink-400" />
                <h3 className="text-lg font-bold text-white">Merge Athletes</h3>
              </div>
              <button onClick={() => setShowConfirm(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-200 font-medium">This action cannot be undone</p>
                  <p className="text-xs text-amber-300/70 mt-1">
                    All measurements, athletic tests, medical records, alerts and ratings from the deleted athlete
                    will be transferred to the kept athlete. The deleted athlete will be archived.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-zinc-400 mb-4">Select which athlete to keep (recommend the one with more data):</p>

            <div className="space-y-3">
              {[selectedPair.child1, selectedPair.child2].map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => setKeepChildId(c.id)}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    keepChildId === c.id ? 'border-green-500 bg-green-500/10' : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                  }`}
                  data-testid={`keep-child-${i + 1}-btn`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white font-bold">
                        {c.first_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-semibold text-white">{c.full_name}</p>
                        <p className="text-sm text-zinc-400 flex items-center gap-2">
                          <Calendar className="h-3 w-3" /> {c.date_of_birth || 'No DOB'}
                        </p>
                        <p className="text-xs text-zinc-500 mt-1">
                          {c.measurements_count} measurements · {c.tests_count} tests
                          {c.team_name ? ` · ${c.team_name}` : ''}
                        </p>
                      </div>
                    </div>
                    {keepChildId === c.id && <Check className="h-6 w-6 text-green-400" />}
                  </div>
                </button>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowConfirm(false)} className="flex-1 border-zinc-700">
                Cancel
              </Button>
              <Button
                onClick={merge}
                disabled={merging || !keepChildId}
                className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white disabled:opacity-50"
                data-testid="confirm-athlete-merge-btn"
              >
                {merging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <GitMerge className="h-4 w-4 mr-2" />}
                Confirm Merge
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
